import {
  Button,
  Chip,
  Divider,
  EButtonColorVariant,
  EChipSizeVariant,
  EHeadingStyleVariant,
  EIconButtonVariant,
  GeneralCaretLeftIcon,
  Heading,
  IconButton,
  LinkButton,
  Modal,
} from '@outdoorsyco/bonfire';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { useIntl } from 'react-intl';

import { DatePickerModalContent } from '@/components/common/DatePickerModal/DatePickerModalContent';
import { useInitialDates } from '@/hooks/useInitialInputs';
import { IHeaderSearchFilterDates } from '@/redux/modules/globalHeader';

type TMobileSearchDatesModalContentProps = {
  onCloseHandler: () => void;
  dates?: IHeaderSearchFilterDates;
  onChangeDates?: (dates?: IHeaderSearchFilterDates) => void;
  disabledDays?: DayPickerProps['disabled'];
};

interface IDates {
  from?: Date;
  to?: Date;
  flexible_days?: number;
}

export const MobileSearchDatesModalContent = ({
  onCloseHandler,
  dates,
  onChangeDates,
  disabledDays = [],
}: TMobileSearchDatesModalContentProps) => {
  const intl = useIntl();
  const [selectedDates, setSelectedDates] = useState<IDates>({
    from: dates?.from,
    to: dates?.to,
    flexible_days: dates?.flexible_days,
  });
  const { hasChanges, isInResetState, resetToInitial } = useInitialDates(
    {
      from: selectedDates?.from,
      to: selectedDates?.to,
      flexible_days: selectedDates?.flexible_days,
    },
    true,
  );

  const handleConfirmDates = () => {
    if (selectedDates.from && !selectedDates.to) {
      return;
    }

    onChangeDates?.(selectedDates);
    onCloseHandler();
  };

  const handleChangeFlexibleDays = (flexible_days?: number) => {
    setSelectedDates({ ...selectedDates, flexible_days });
  };

  return (
    <>
      <Modal.Header inlineHeader={true} className="flex items-center gap-2 !mt-6">
        <IconButton
          icon={GeneralCaretLeftIcon}
          onClick={() => {
            const initial = resetToInitial();
            setSelectedDates({
              from: initial?.from,
              to: initial.to,
              flexible_days: initial?.flexible_days,
            });
            onCloseHandler();
          }}
          className="text-2xl"
          variant={EIconButtonVariant.Ghost}
        />
        <Heading variant={EHeadingStyleVariant.H6}>
          {intl.formatMessage({ defaultMessage: 'Select your dates', id: 'rqvhJC' })}
        </Heading>
      </Modal.Header>
      <Modal.Content className="mt-2">
        <div>
          <DatePickerModalContent
            numberOfMonths={24}
            isStacked={true}
            onChange={({ from, to }: IDates) => setSelectedDates({ ...selectedDates, from, to })}
            dateTo={selectedDates.to}
            dateFrom={selectedDates.from}
            toMonth={dayjs().add(2, 'year').toDate()}
            disabledDays={disabledDays}
          />
        </div>
      </Modal.Content>
      <Modal.Footer className="p-0">
        <div className="w-full">
          <Divider />
          <div className="flex items-center justify-between p-4">
            <div className="flex p-1 overflow-x-auto scrollbar-none gap-2">
              <Chip
                content={intl.formatMessage({ defaultMessage: 'Match exact dates', id: 'uhcpp1' })}
                sizeVariant={EChipSizeVariant.Small}
                onClick={() => handleChangeFlexibleDays(undefined)}
                active={!selectedDates?.flexible_days}
                withBoxShadow={false}
                contentClassName="whitespace-nowrap [&>p]:font-medium"
              />
              <Chip
                content={intl.formatMessage({ defaultMessage: '± 1 day', id: '9IdT1V' })}
                sizeVariant={EChipSizeVariant.Small}
                onClick={() => handleChangeFlexibleDays(1)}
                active={selectedDates?.flexible_days === 1}
                withBoxShadow={false}
                contentClassName="whitespace-nowrap [&>p]:font-medium"
              />
              <Chip
                content={intl.formatMessage({ defaultMessage: '± 3 days', id: 'r4uykC' })}
                sizeVariant={EChipSizeVariant.Small}
                onClick={() => handleChangeFlexibleDays(3)}
                active={selectedDates?.flexible_days === 3}
                withBoxShadow={false}
                contentClassName="whitespace-nowrap [&>p]:font-medium"
              />
            </div>
          </div>
          <Divider />
          <div className="flex items-center justify-between px-6 py-4">
            <LinkButton
              label={intl.formatMessage({ defaultMessage: 'Clear', id: '/GCoTA' })}
              onClick={() => setSelectedDates({})}
              disabled={isInResetState}
            />
            <Button
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })}
              onClick={handleConfirmDates}
              disabled={!hasChanges}
            />
          </div>
        </div>
      </Modal.Footer>
    </>
  );
};
